/**
 * Швидке створення КАРТКИ НА з рядка «Необоротні активи» прихода
 * (2026-07-31; 2026-08-31 — вибір виду: ОЗ / НМА / малоцінка, рішення
 * власника: первинна КАРТКА, «Введення в експлуатацію» — окремим
 * документом; кнопка-перехід одразу після створення).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { assetGroups, depreciationMethods, fixedAssets, intangibleAssets, lowValueAssets } from '@/api/assets';

type Kind = 'FIXED_ASSET' | 'INTANGIBLE' | 'LOW_VALUE';

export default function QuickFixedAssetModal({
  organizationId, defaultName, defaultCost, onClose,
}: {
  organizationId: string;
  defaultName: string;
  defaultCost: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [kind, setKind] = useState<Kind>('FIXED_ASSET');
  const [name, setName] = useState(defaultName);
  const [groupId, setGroupId] = useState('');
  const [methodId, setMethodId] = useState('');
  const [cost, setCost] = useState(defaultCost);
  const [lifeMonths, setLifeMonths] = useState('60');
  const [expenseAcc, setExpenseAcc] = useState('92');
  const [writeOff, setWriteOff] = useState<'AT_USE_100' | 'SPLIT_50_50'>('AT_USE_100');
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ id: string; label: string } | null>(null);
  const isLv = kind === 'LOW_VALUE';

  const groupsQ = useQuery({
    queryKey: ['asset-groups', kind],
    queryFn: () => assetGroups.list({ kind: kind as never, limit: 200 }),
    enabled: !isLv,
  });
  const methodsQ = useQuery({
    queryKey: ['depreciation-methods'],
    queryFn: () => depreciationMethods.list({ limit: 100 }),
  });

  const create = useMutation({
    mutationFn: async () => {
      if (isLv) {
        return lowValueAssets.create({
          organization_id: organizationId, inventory_number: null,
          name: name.trim(), primary_cost: cost,
          write_off_method: writeOff, track_mode: 'INDIVIDUAL', quantity: 1,
          expense_account_code: expenseAcc,
        });
      }
      const p = {
        organization_id: organizationId, inventory_number: null,
        name: name.trim(), asset_group_id: groupId,
        depreciation_method_id: methodId, primary_cost: cost,
        useful_life_months: Number(lifeMonths) || 60,
        expense_account_code: expenseAcc,
      };
      return kind === 'FIXED_ASSET' ? fixedAssets.create(p) : intangibleAssets.create(p);
    },
    onSuccess: (a) => {
      qc.invalidateQueries({ queryKey: ['fixed-assets-v2'] });
      qc.invalidateQueries({ queryKey: ['assets-registry'] });
      setCreated({ id: a.id, label: `${a.inventory_number ?? ''} ${a.name}`.trim() });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e)),
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div className="card" style={{ width: 560, margin: 0 }}
        onClick={(e) => e.stopPropagation()}>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Нова картка НА (з рядка прихода)</h3>
          <button style={{ marginLeft: 'auto' }} onClick={onClose}>закрити</button>
        </div>
        {created ? (
          <>
            <p style={{ color: 'var(--badge-green-fg)' }}>
              ✓ Картку «{created.label}» створено (статус: не введено в експлуатацію).
            </p>
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
              Наступний крок — документ «Введення в експлуатацію»: зробить
              проводку {isLv ? 'списання малоцінки у витрати' : 'Дт 10х/12х — Кт 152/154'} і
              {isLv ? ' поставить на облік' : ' запустить амортизацію'}.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="primary"
                onClick={() => navigate(`/assets/commissioning/new?asset_id=${created.id}&kind=${kind}&organization_id=${organizationId}`)}>
                ввести в експлуатацію
              </button>
              <button onClick={onClose}>готово</button>
            </div>
          </>
        ) : (
          <>
            <div className="field">
              <label>Вид активу</label>
              <select value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
                <option value="FIXED_ASSET">Основний засіб (ОЗ)</option>
                <option value="INTANGIBLE">Нематеріальний актив (НМА)</option>
                <option value="LOW_VALUE">Малоцінка (МШП / МНМА)</option>
              </select>
            </div>
            <div className="field">
              <label>Назва <span style={{ color: 'red' }}>*</span></label>
              <input value={name} autoFocus onChange={(e) => setName(e.target.value)} />
            </div>
            {!isLv && (
              <div className="doc-grid">
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>Група <span style={{ color: 'red' }}>*</span></label>
                  <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                    <option value="">— оберіть —</option>
                    {(groupsQ.data?.items ?? []).map((g) => (
                      <option key={g.id} value={g.id}>{g.name_uk}</option>
                    ))}
                  </select>
                </div>
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>Метод амортизації <span style={{ color: 'red' }}>*</span></label>
                  <select value={methodId} onChange={(e) => setMethodId(e.target.value)}>
                    <option value="">— оберіть —</option>
                    {(methodsQ.data?.items ?? []).map((m) => (
                      <option key={m.id} value={m.id}>{m.name_uk}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            <div className="doc-grid">
              <div className="field" style={{ gridColumn: 'span 4' }}>
                <label>Первісна вартість</label>
                <input type="number" step="0.01" value={cost}
                  style={{ textAlign: 'right' }}
                  onChange={(e) => setCost(e.target.value)} />
              </div>
              {!isLv ? (
                <div className="field" style={{ gridColumn: 'span 4' }}>
                  <label>Строк корисного викор., міс</label>
                  <input type="number" min="1" value={lifeMonths}
                    onChange={(e) => setLifeMonths(e.target.value)} />
                </div>
              ) : (
                <div className="field" style={{ gridColumn: 'span 4' }}>
                  <label>Метод списання</label>
                  <select value={writeOff} onChange={(e) => setWriteOff(e.target.value as never)}>
                    <option value="AT_USE_100">100% при введенні</option>
                    <option value="SPLIT_50_50">50/50</option>
                  </select>
                </div>
              )}
              <div className="field" style={{ gridColumn: 'span 4' }}>
                <label>{isLv ? 'Рахунок витрат' : 'Рахунок витрат амортизації'}</label>
                <select value={expenseAcc} onChange={(e) => setExpenseAcc(e.target.value)}>
                  <option value="92">92 — Адміністративні</option>
                  <option value="91">91 — Загальновиробничі</option>
                  <option value="93">93 — Збут</option>
                  <option value="94">94 — Інші операційні</option>
                  <option value="23">23 — Виробництво</option>
                </select>
              </div>
            </div>
            <p style={{ color: 'var(--muted)', fontSize: '0.82rem', margin: '0.25rem 0 0.6rem' }}>
              Інвентарний номер призначиться автоматично; рахунки обліку —
              з налаштувань «Рахунки обліку НА» (перебиваються у картці).
            </p>
            {error && <div style={{ color: '#dc2626', marginBottom: 8 }}>{String(error)}</div>}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="primary"
                disabled={!name.trim() || (!isLv && (!groupId || !methodId)) || create.isPending}
                title={!name.trim() ? 'Вкажіть назву'
                  : (!isLv && !groupId) ? 'Оберіть групу'
                  : (!isLv && !methodId) ? 'Оберіть метод амортизації' : undefined}
                onClick={() => create.mutate()}>
                {create.isPending ? 'створюється…' : 'створити картку'}
              </button>
              <button onClick={onClose}>скасувати</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
